import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';

import { FormRichTextField, FormTextField } from '../../../../../common';
import { EMPTY_ASSESSMENT_METHOD_VISUAL_VALUES } from '../constants';
import { useSaveAssessmentMethodVisual } from '../hooks';
import {
  ASSESSMENT_METHOD_VISUAL_CARD_SX,
  ASSESSMENT_METHOD_VISUAL_IMAGE_SX,
  ASSESSMENT_METHOD_VISUAL_PREVIEW_SX,
} from '../styles';
import {
  getAssessmentMethodVisualPreviewUrl,
  isAssessmentMethodVisualPreviewable,
  mapAssessmentMethodVisualToValues,
} from '../utils';
import { ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA } from '../validationSchema';
import AssessmentMethodVisualCardActions from './AssessmentMethodVisualCardActions';

import type { AssessmentMethodVisualCardProps } from './types';

export default function AssessmentMethodVisualCard({
  methodId,
  visual,
  pendingFile,
  pendingPreviewUrl,
  disabled,
  isSaving,
  onCreate,
  onUpdate,
  onUpdateFile,
  onReplacePendingFile,
  onRequestDelete,
  onCreated,
  readOnly,
}: AssessmentMethodVisualCardProps) {
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [previewRevision, setPreviewRevision] = useState(0);
  const localPreviewUrlRef = useRef<string | null>(null);
  const initialValues = useMemo(
    () => (visual ? mapAssessmentMethodVisualToValues(visual) : EMPTY_ASSESSMENT_METHOD_VISUAL_VALUES),
    [visual],
  );
  const formik = useFormik({
    initialValues,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA,
    onSubmit: () => undefined,
  });
  const { saveField, saveFile } = useSaveAssessmentMethodVisual({
    methodId,
    visualId: visual?.id,
    pendingFile,
    formik,
    initialValues,
    onCreate,
    onUpdate,
    onUpdateFile,
    onCreated,
  });
  const revokeLocalPreviewUrl = useCallback(() => {
    if (!localPreviewUrlRef.current) return;
    URL.revokeObjectURL(localPreviewUrlRef.current);
    localPreviewUrlRef.current = null;
  }, []);
  useEffect(() => revokeLocalPreviewUrl, [revokeLocalPreviewUrl]);
  const handleSelectReplacementFile = useCallback(
    async (file: File) => {
      if (!visual) {
        onReplacePendingFile?.(file);
        return;
      }
      revokeLocalPreviewUrl();
      const previewUrl = URL.createObjectURL(file);
      localPreviewUrlRef.current = previewUrl;
      setLocalPreviewUrl(previewUrl);
      const saved = await saveFile(file);
      if (!saved) {
        revokeLocalPreviewUrl();
        setLocalPreviewUrl(null);
        return;
      }
      setPreviewRevision((revision) => revision + 1);
      revokeLocalPreviewUrl();
      setLocalPreviewUrl(null);
    },
    [onReplacePendingFile, revokeLocalPreviewUrl, saveFile, visual],
  );
  const handleDelete = useCallback(() => {
    onRequestDelete(visual ?? 'pending');
  }, [onRequestDelete, visual]);
  const previewUrl =
    localPreviewUrl ??
    (visual ? getAssessmentMethodVisualPreviewUrl(visual.url, previewRevision) : pendingPreviewUrl ?? '');

  return (
    <Box sx={ASSESSMENT_METHOD_VISUAL_CARD_SX}>
      {readOnly ? null : (
        <AssessmentMethodVisualCardActions
          disabled={disabled}
          isSaving={isSaving}
          onDelete={handleDelete}
          onSelectFile={handleSelectReplacementFile}
        />
      )}
      <Box sx={ASSESSMENT_METHOD_VISUAL_PREVIEW_SX}>
        {previewUrl && isAssessmentMethodVisualPreviewable(previewUrl) ? (
          <Box alt="visual" component="img" src={previewUrl} sx={ASSESSMENT_METHOD_VISUAL_IMAGE_SX} />
        ) : (
          <Typography fontWeight="bold">Preview not available</Typography>
        )}
      </Box>
      <Stack gap={2}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Visual title"
          name="title"
          onBlur={saveField}
          placeholder="Enter visual title"
          readOnly={readOnly}
          required
        />
        <FormRichTextField
          disabled={disabled}
          formik={formik}
          label="Visual analysis"
          name="analysis"
          onBlur={saveField}
          placeholder="Describe the visual analysis"
          readOnly={readOnly}
          required
        />
      </Stack>
    </Box>
  );
}
